import { useEffect, useRef, useState, useCallback, type RefObject } from 'react'
import { createConsumer, Cable } from '@rails/actioncable'

export interface BrowserCommand {
  action: 'click' | 'type' | 'select' | 'waitFor' | 'navigate' | 'getElements' | 'screenshot' | 'getValue' | 'isVisible' | 'reload' | 'setViewport'
  selector?: string
  text?: string
  path?: string
  timeout?: number
  continueOnError?: boolean
  /** For setViewport action: 'desktop' or 'mobile' */
  viewport?: 'desktop' | 'mobile'
}

export interface BrowserCommandResult {
  step: number
  action: string
  success: boolean
  error?: string
  element?: string
  value?: string
}

export interface ActionLogEntry {
  timestamp: Date
  type: 'command_received' | 'command_executing' | 'command_complete' | 'results_sent' | 'error'
  message: string
  data?: any
}

interface BrowserUseOptions {
  projectId: number
  iframeRef: RefObject<HTMLIFrameElement | null>
  /** Mobile app iframe ref (Expo Web) - commands go here when appType is 'mobile' */
  mobileIframeRef?: RefObject<HTMLIFrameElement | null>
  /** Current app type - determines which iframe receives commands */
  appType?: 'web' | 'mobile'
  /** Whether sketch/canvas view is currently active (iframes are unmounted) */
  isSketchViewActive?: boolean
  onTestingStart?: () => void
  onTestingEnd?: (results: BrowserCommandResult[]) => void
  /** Callback for reload preview action */
  onReload?: () => void
  /** Callback for setViewport action */
  onSetViewport?: (viewport: 'desktop' | 'mobile') => void
  /** Callback for switching to mobile app preview */
  onSwitchToMobile?: () => Promise<boolean>
  /** Callback for switching to web app preview */
  onSwitchToWeb?: (viewport?: 'desktop' | 'mobile') => Promise<boolean>
  /** Callback for switching to sketch view */
  onSwitchToSketch?: () => Promise<boolean>
}

/**
 * Hook to handle browser use testing via ActionCable + postMessage
 *
 * Flow:
 * 1. cayu-pilot sends commands to cayu-ai-mvp API
 * 2. API broadcasts via ActionCable to this hook
 * 3. Hook forwards commands to iframe via postMessage
 * 4. iframe executes and returns results via postMessage
 * 5. Hook sends results back via ActionCable to complete the API request
 */
export function useBrowserUse({
  projectId,
  iframeRef,
  mobileIframeRef,
  appType = 'web',
  isSketchViewActive = false,
  onTestingStart,
  onTestingEnd,
  onReload,
  onSetViewport,
  onSwitchToMobile,
  onSwitchToWeb,
  onSwitchToSketch,
}: BrowserUseOptions) {
  const [isActive, setIsActive] = useState(false)
  const [actionLog, setActionLog] = useState<ActionLogEntry[]>([])
  const [currentAction, setCurrentAction] = useState<string | null>(null)

  const consumerRef = useRef<Cable | null>(null)
  const channelRef = useRef<any>(null)
  const currentSessionRef = useRef<string | null>(null)

  // Store callbacks and dynamic values in refs to avoid recreating subscription
  const onTestingStartRef = useRef(onTestingStart)
  const onTestingEndRef = useRef(onTestingEnd)
  const onReloadRef = useRef(onReload)
  const onSetViewportRef = useRef(onSetViewport)
  const onSwitchToMobileRef = useRef(onSwitchToMobile)
  const onSwitchToWebRef = useRef(onSwitchToWeb)
  const onSwitchToSketchRef = useRef(onSwitchToSketch)
  // These refs ensure the subscription handler always uses the latest values
  const appTypeRef = useRef(appType)
  const mobileIframeRefRef = useRef(mobileIframeRef)
  const isSketchViewActiveRef = useRef(isSketchViewActive)

  useEffect(() => {
    onTestingStartRef.current = onTestingStart
    onTestingEndRef.current = onTestingEnd
    onReloadRef.current = onReload
    onSetViewportRef.current = onSetViewport
    onSwitchToMobileRef.current = onSwitchToMobile
    onSwitchToWebRef.current = onSwitchToWeb
    onSwitchToSketchRef.current = onSwitchToSketch
    appTypeRef.current = appType
    mobileIframeRefRef.current = mobileIframeRef
    isSketchViewActiveRef.current = isSketchViewActive
  })

  const addLogEntry = useCallback((type: ActionLogEntry['type'], message: string, data?: any) => {
    setActionLog(prev => [...prev, {
      timestamp: new Date(),
      type,
      message,
      data
    }])
  }, [])

  const clearLog = useCallback(() => {
    setActionLog([])
  }, [])

  // Handle results coming back from iframe
  const handleIframeMessage = useCallback((event: MessageEvent) => {
    // Handle browser use results from iframe
    // Note: sendToParent wraps data in event.data.data
    if (event.data?.type === 'cayu:browser-results') {
      const { sessionId, results: iframeResults, errors, screenshot } = event.data.data || event.data

      // Verify this is for our current session
      if (sessionId !== currentSessionRef.current) {
        console.warn('[BrowserUse] Received results for unknown session:', sessionId)
        return
      }

      // Merge preview control results with iframe results
      const previewResultsData = (window as any).__cayuPreviewResults
      let mergedResults = iframeResults
      if (previewResultsData?.sessionId === sessionId && previewResultsData.results.length > 0) {
        // Combine and sort by step number to maintain original order
        mergedResults = [...previewResultsData.results, ...iframeResults].sort((a, b) => a.step - b.step)
        // Clean up
        delete (window as any).__cayuPreviewResults
      }

      console.log('[BrowserUse] Received results from iframe:', { sessionId, results: mergedResults, errors })
      addLogEntry('results_sent', `Completed ${mergedResults.length} commands`, { results: mergedResults, errors })

      // Send results back to server via ActionCable
      console.log('[BrowserUse] channelRef.current:', channelRef.current)
      console.log('[BrowserUse] currentSessionRef.current:', currentSessionRef.current)

      if (channelRef.current) {
        const payload = {
          session_id: sessionId,
          results: {
            results: mergedResults,
            errors: errors || {},
            screenshot
          }
        }
        console.log('[BrowserUse] Sending results via ActionCable perform:', payload)
        try {
          channelRef.current.perform('results', payload)
          console.log('[BrowserUse] ActionCable perform completed')
        } catch (err) {
          console.error('[BrowserUse] ActionCable perform failed:', err)
        }
      } else {
        console.error('[BrowserUse] No channel reference to send results!')
      }

      // Reset state
      setIsActive(false)
      setCurrentAction(null)
      currentSessionRef.current = null
      onTestingEndRef.current?.(mergedResults)
    }

    // Handle individual action progress updates
    if (event.data?.type === 'cayu:browser-action-progress') {
      const { action, step, total } = event.data.data || event.data
      setCurrentAction(`${action} (${step + 1}/${total})`)
      addLogEntry('command_executing', `Executing: ${action}`, { step, total })
    }
  }, [addLogEntry])

  // Set up message listener for iframe responses
  useEffect(() => {
    window.addEventListener('message', handleIframeMessage)
    return () => window.removeEventListener('message', handleIframeMessage)
  }, [handleIframeMessage])

  // Set up ActionCable subscription
  useEffect(() => {
    consumerRef.current = createConsumer()

    channelRef.current = consumerRef.current.subscriptions.create(
      {
        channel: 'BrowserUseChannel',
        project_id: projectId
      },
      {
        received: (data: { type: string; session_id: string; commands?: BrowserCommand[]; include_screenshot?: boolean; preview_action?: string; viewport?: 'desktop' | 'mobile' }) => {
          console.log('[BrowserUse] Received from ActionCable:', data)

          if (data.type === 'execute') {
            const { session_id, commands, include_screenshot } = data

            // Guard: commands must exist for execute type
            if (!commands) {
              console.error('[BrowserUse] Execute message missing commands')
              return
            }

            // Store session ID for matching results
            currentSessionRef.current = session_id

            // Update state
            setIsActive(true)
            addLogEntry('command_received', `Received ${commands.length} commands to execute`, { commands })
            onTestingStartRef.current?.()

            // Separate preview controls from browser commands
            // Only setViewport is handled in parent (it controls the iframe container size)
            // Reload is handled by iframe's browser-use-agent using Inertia router.reload()
            // Navigate is handled by iframe's browser-use-agent using Inertia router.visit()
            const previewControls = commands.filter(cmd => cmd.action === 'setViewport')
            const browserCommands = commands.filter(cmd => cmd.action !== 'setViewport')

            // Execute preview controls locally (only setViewport now)
            const previewResults: BrowserCommandResult[] = []
            previewControls.forEach((cmd) => {
              const originalIndex = commands.findIndex(c => c === cmd)
              if (cmd.action === 'setViewport') {
                console.log('[BrowserUse] Executing setViewport locally:', cmd.viewport)
                addLogEntry('command_executing', `Executing: setViewport (${cmd.viewport})`, { step: originalIndex })
                if (onSetViewportRef.current && cmd.viewport) {
                  onSetViewportRef.current(cmd.viewport)
                  previewResults.push({
                    step: originalIndex,
                    action: 'setViewport',
                    success: true,
                    value: cmd.viewport
                  })
                } else {
                  previewResults.push({
                    step: originalIndex,
                    action: 'setViewport',
                    success: false,
                    error: cmd.viewport ? 'Viewport handler not configured' : 'Viewport value not specified'
                  })
                }
              }
            })

            // If only preview controls, send results immediately
            if (browserCommands.length === 0) {
              console.log('[BrowserUse] Only preview controls, sending results immediately')
              addLogEntry('results_sent', `Completed ${previewResults.length} preview control commands`, { results: previewResults })

              if (channelRef.current) {
                channelRef.current.perform('results', {
                  session_id,
                  results: {
                    results: previewResults,
                    errors: {}
                  }
                })
              }

              // Small delay before resetting state to ensure React registers the active state
              // This allows PreviewPane's useEffect to see isBrowserUseTesting=true and hide hints
              setTimeout(() => {
                setIsActive(false)
                setCurrentAction(null)
                currentSessionRef.current = null
                onTestingEndRef.current?.(previewResults)
              }, 100)
              return
            }

            // Store preview results to merge later when iframe results come back
            // We'll use a closure in handleIframeMessage via the session ref
            (window as any).__cayuPreviewResults = {
              sessionId: session_id,
              results: previewResults
            }

            // Helper to send error results and cleanup
            const sendErrorResults = (errorMessage: string) => {
              console.error('[BrowserUse]', errorMessage)
              addLogEntry('error', errorMessage)
              if (channelRef.current) {
                channelRef.current.perform('results', {
                  session_id,
                  results: {
                    results: previewResults,
                    errors: { sdk: errorMessage }
                  }
                })
              }
              setIsActive(false)
              setCurrentAction(null)
              currentSessionRef.current = null
              onTestingEndRef.current?.(previewResults)
            }

            const forwardCommandsToIframe = () => {
              // Choose iframe based on current app type (use ref for latest value)
              // When appType is 'mobile', use mobileIframeRef if available
              const currentAppType = appTypeRef.current
              const currentMobileIframeRef = mobileIframeRefRef.current
              const activeIframe = currentAppType === 'mobile' && currentMobileIframeRef?.current
                ? currentMobileIframeRef.current
                : iframeRef.current

              if (activeIframe?.contentWindow) {
                console.log('[BrowserUse] Forwarding browser commands to iframe:', browserCommands, 'includeScreenshot:', include_screenshot, 'appType:', currentAppType)
                activeIframe.contentWindow.postMessage({
                  type: 'cayu:browser-command',
                  data: {
                    sessionId: session_id,
                    commands: browserCommands,
                    includeScreenshot: include_screenshot || false
                  }
                }, '*')
              } else {
                sendErrorResults(`No iframe available to execute commands (appType: ${currentAppType})`)
              }
            }

            // Ping SDK and wait for pong with timeout
            // Returns: { alive: boolean, healthy: boolean, error?: string, errorType?: string }
            const pingSDK = (timeout: number): Promise<{ alive: boolean; healthy: boolean; error?: string; errorType?: string }> => {
              return new Promise((resolve) => {
                const handlePong = (event: MessageEvent) => {
                  if (event.data?.type === 'cayu:browser-pong') {
                    window.removeEventListener('message', handlePong)
                    const data = event.data.data || event.data
                    resolve({
                      alive: true,
                      healthy: data.healthy !== false, // Default to healthy if not specified
                      error: data.error,
                      errorType: data.errorType
                    })
                  }
                }

                window.addEventListener('message', handlePong)

                // Use correct iframe based on app type (use refs for latest values)
                const currentAppType = appTypeRef.current
                const currentMobileIframeRef = mobileIframeRefRef.current
                const activeIframe = currentAppType === 'mobile' && currentMobileIframeRef?.current
                  ? currentMobileIframeRef.current
                  : iframeRef.current

                if (activeIframe?.contentWindow) {
                  activeIframe.contentWindow.postMessage({ type: 'cayu:browser-ping' }, '*')
                }

                setTimeout(() => {
                  window.removeEventListener('message', handlePong)
                  resolve({ alive: false, healthy: false })
                }, timeout)
              })
            }

            // Wait for SDK ready signal after reload
            const waitForSdkReady = (timeout: number): Promise<boolean> => {
              return new Promise((resolve) => {
                const handleReady = (event: MessageEvent) => {
                  if (event.data?.type === 'cayu:sdk-ready') {
                    window.removeEventListener('message', handleReady)
                    resolve(true)
                  }
                }

                window.addEventListener('message', handleReady)

                setTimeout(() => {
                  window.removeEventListener('message', handleReady)
                  resolve(false)
                }, timeout)
              })
            }

            // Wait for page URL to stabilize (no redirects in progress)
            const waitForUrlStable = async (timeout: number = 1500): Promise<void> => {
              // Use correct iframe based on app type (use refs for latest values)
              const currentAppType = appTypeRef.current
              const currentMobileIframeRef = mobileIframeRefRef.current
              const activeIframe = currentAppType === 'mobile' && currentMobileIframeRef?.current
                ? currentMobileIframeRef.current
                : iframeRef?.current

              if (!activeIframe?.contentWindow) return
              const iframe = activeIframe

              let lastUrl = ''
              const startTime = Date.now()

              while (Date.now() - startTime < timeout) {
                try {
                  const currentUrl = iframe.contentWindow!.location.href
                  if (currentUrl === lastUrl) {
                    // URL hasn't changed in 200ms, consider it stable
                    console.log('[BrowserUse] URL stable:', currentUrl)
                    return
                  }
                  lastUrl = currentUrl
                } catch {
                  // Cross-origin - can't check URL, just wait
                }
                await new Promise(r => setTimeout(r, 200))
              }
              console.log('[BrowserUse] URL stabilization timeout, proceeding anyway')
            }

            // Main execution flow with SDK health check
            const executeWithHealthCheck = async () => {
              // If sketch/canvas view is active, iframes are unmounted — switch to the correct preview first
              if (isSketchViewActiveRef.current) {
                const currentAppType = appTypeRef.current
                console.log('[BrowserUse] Sketch view active, switching to', currentAppType, 'preview first')
                addLogEntry('command_executing', `Switching from canvas to ${currentAppType} preview...`)
                const switchFn = currentAppType === 'mobile' ? onSwitchToMobileRef.current : onSwitchToWebRef.current
                if (switchFn) {
                  await switchFn()
                  // Wait for iframe to mount after view switch
                  await new Promise(r => setTimeout(r, 300))
                } else {
                  sendErrorResults(`No switch handler for ${currentAppType} preview`)
                  return
                }
              }

              // Ping SDK to check if it's alive and healthy
              console.log('[BrowserUse] Pinging SDK...')
              const pingResult = await pingSDK(300)

              if (pingResult.alive && pingResult.healthy) {
                console.log('[BrowserUse] SDK responded to ping and is healthy, forwarding commands')
                forwardCommandsToIframe()
                return
              }

              // Handle based on error type
              if (pingResult.alive && !pingResult.healthy) {
                // SDK responded but app is unhealthy
                if (pingResult.errorType === 'OVERLAY' || pingResult.errorType === 'STORED_ERRORS') {
                  // Error detected - DON'T reload, just return error
                  // Claude can fix it, HMR will apply fix and clear errors
                  console.log('[BrowserUse] Error detected, returning without reload:', pingResult.errorType, pingResult.error)
                  sendErrorResults(pingResult.error || 'Error detected')
                  return
                }

                // Blank page (PAGE_BLANK) - auto-reload to recover
                console.log('[BrowserUse] Page blank, reloading to recover')
                addLogEntry('command_executing', 'Page blank, reloading to recover...')
              } else {
                // SDK not responding at all - reload to try to recover
                console.log('[BrowserUse] SDK not responding, reloading iframe to recover...')
                addLogEntry('command_executing', 'SDK unresponsive, reloading page to recover...')
              }

              if (onReloadRef.current) {
                onReloadRef.current()
              } else {
                sendErrorResults('Reload handler not configured')
                return
              }

              // Wait for SDK to be ready after reload
              const ready = await waitForSdkReady(3000)

              if (ready) {
                // Wait for any redirects to complete (e.g., ?return_to= handling)
                await waitForUrlStable(1500)

                // Re-ping to check if app is healthy (might have error overlay after reload)
                console.log('[BrowserUse] SDK ready after reload, re-pinging to check health...')
                const healthCheck = await pingSDK(500)

                if (healthCheck.alive && healthCheck.healthy) {
                  console.log('[BrowserUse] App healthy after reload, forwarding commands')
                  forwardCommandsToIframe()
                } else if (healthCheck.errorType === 'OVERLAY' || healthCheck.errorType === 'STORED_ERRORS') {
                  // Error detected after reload - return error
                  console.log('[BrowserUse] Error after reload:', healthCheck.errorType, healthCheck.error)
                  sendErrorResults(healthCheck.error || 'Error detected after reload')
                } else {
                  // Still unhealthy after reload (likely build error that crashes before our error handler initializes)
                  // Return PAGE_BLANK so mcp_tools.py checks build errors
                  console.log('[BrowserUse] Page still unhealthy after reload, returning PAGE_BLANK for build check')
                  sendErrorResults('PAGE_BLANK')
                }
              } else {
                sendErrorResults('Page unresponsive - SDK failed to initialize after reload. Check: npm run build, Rails logs, browser console.')
              }
            }

            executeWithHealthCheck()
          }

          // Handle preview_control messages (from preview_control MCP tool)
          if (data.type === 'preview_control') {
            const { session_id, preview_action, viewport } = data

            console.log('[BrowserUse] Received preview_control:', { preview_action, viewport })
            addLogEntry('command_received', `Received preview control: ${preview_action}`, { preview_action, viewport })

            const sendPreviewResult = (success: boolean, error?: string) => {
              if (channelRef.current) {
                channelRef.current.perform('results', {
                  session_id,
                  results: { success, error }
                })
              }
            }

            if (preview_action === 'switch_to_mobile') {
              if (onSwitchToMobileRef.current) {
                addLogEntry('command_executing', 'Switching to mobile app preview...')
                onSwitchToMobileRef.current()
                  .then((success) => {
                    if (success) {
                      addLogEntry('command_complete', 'Switched to mobile app preview')
                      sendPreviewResult(true)
                    } else {
                      addLogEntry('error', 'Failed to switch to mobile app preview')
                      sendPreviewResult(false, 'Mobile app not available or not ready')
                    }
                  })
                  .catch((err) => {
                    addLogEntry('error', `Error switching to mobile: ${err.message}`)
                    sendPreviewResult(false, err.message)
                  })
              } else {
                addLogEntry('error', 'Switch to mobile handler not configured')
                sendPreviewResult(false, 'Switch to mobile handler not configured')
              }
            } else if (preview_action === 'switch_to_web') {
              if (onSwitchToWebRef.current) {
                const viewportLabel = viewport ? ` (${viewport})` : ''
                addLogEntry('command_executing', `Switching to web app preview${viewportLabel}...`)
                onSwitchToWebRef.current(viewport)
                  .then((success) => {
                    if (success) {
                      addLogEntry('command_complete', `Switched to web app preview${viewportLabel}`)
                      sendPreviewResult(true)
                    } else {
                      addLogEntry('error', 'Failed to switch to web app preview')
                      sendPreviewResult(false, 'Web app not available or not ready')
                    }
                  })
                  .catch((err) => {
                    addLogEntry('error', `Error switching to web: ${err.message}`)
                    sendPreviewResult(false, err.message)
                  })
              } else {
                addLogEntry('error', 'Switch to web handler not configured')
                sendPreviewResult(false, 'Switch to web handler not configured')
              }
            } else if (preview_action === 'switch_to_sketch') {
              if (onSwitchToSketchRef.current) {
                addLogEntry('command_executing', 'Switching to sketch view...')
                onSwitchToSketchRef.current()
                  .then((success) => {
                    if (success) {
                      addLogEntry('command_complete', 'Switched to sketch view')
                      sendPreviewResult(true)
                    } else {
                      addLogEntry('error', 'Failed to switch to sketch view')
                      sendPreviewResult(false, 'Sketch view not available')
                    }
                  })
                  .catch((err) => {
                    addLogEntry('error', `Error switching to sketch: ${err.message}`)
                    sendPreviewResult(false, err.message)
                  })
              } else {
                addLogEntry('error', 'Switch to sketch handler not configured')
                sendPreviewResult(false, 'Switch to sketch handler not configured')
              }
            } else {
              addLogEntry('error', `Unknown preview control action: ${preview_action}`)
              sendPreviewResult(false, `Unknown action: ${preview_action}`)
            }
          }
        },
        connected: () => {
          console.log('[BrowserUse] Connected to BrowserUseChannel for project', projectId)
        },
        disconnected: () => {
          console.log('[BrowserUse] Disconnected from BrowserUseChannel')
        },
        rejected: () => {
          console.error('[BrowserUse] BrowserUseChannel subscription rejected')
        }
      }
    )

    return () => {
      if (channelRef.current) {
        console.log('[BrowserUse] Unsubscribing from BrowserUseChannel')
        channelRef.current.unsubscribe()
      }
      if (consumerRef.current) {
        consumerRef.current.disconnect()
      }
    }
  }, [projectId, iframeRef, addLogEntry])

  // Stop testing (cancel current session)
  const stopTesting = useCallback(() => {
    if (currentSessionRef.current && channelRef.current) {
      // Send cancellation to server
      channelRef.current.perform('results', {
        session_id: currentSessionRef.current,
        results: {
          results: [],
          errors: { cancelled: 'Testing cancelled by user' }
        }
      })

      // Send cancel message to iframe (send to both web and mobile if available)
      const webIframe = iframeRef.current
      if (webIframe?.contentWindow) {
        webIframe.contentWindow.postMessage({
          type: 'cayu:browser-cancel',
          data: {
            sessionId: currentSessionRef.current
          }
        }, '*')
      }

      // Also send to mobile iframe if available
      const mobileIframe = mobileIframeRef?.current
      if (mobileIframe?.contentWindow) {
        mobileIframe.contentWindow.postMessage({
          type: 'cayu:browser-cancel',
          data: {
            sessionId: currentSessionRef.current
          }
        }, '*')
      }
    }

    setIsActive(false)
    setCurrentAction(null)
    currentSessionRef.current = null
    addLogEntry('error', 'Testing cancelled by user')
  }, [iframeRef, mobileIframeRef, addLogEntry])

  return {
    isActive,
    actionLog,
    currentAction,
    clearLog,
    stopTesting
  }
}
